<template>
  <div class="video-container"
       @click="emit('toggle-controls')"
       @mousemove="emit('mouse-move')"
       @mouseleave="emit('mouse-leave')"
       @touchstart="emit('touch-start')"
       @touchend="emit('touch-end')">

    <!-- Удаленное видео (большое) -->
    <div class="video-wrapper remote">
      <video
        ref="remoteVideoRef"
        autoplay
        playsinline
        :class="{ 'video-hidden': !remoteStream }"
      ></video>

      <!-- Плейсхолдер когда нет собеседника -->
      <div v-if="!remoteStream" class="video-placeholder">
        <div class="placeholder-icon-remote">
          <i class="fas fa-user-slash"></i>
        </div>
        <p>Ожидание собеседника...</p>
      </div>

      <div v-if="remoteVideoMuted" class="video-placeholder">
        <div class="placeholder-icon">
          <i :class="videoMuted ? 'fas fa-video-slash' : 'fas fa-video'"></i>
        </div>
        <p>Видео выключено</p>
      </div>
      <div v-if="remoteAudioMuted" class="audio-placeholder">
        <div class="placeholder-icon">
          <i class="fas fa-microphone-slash"></i>
        </div>
        <p>Аудио выключено</p>
      </div>

      <!-- Индикатор переподключения -->
      <div v-if="isReconnecting" class="reconnecting-indicator">
        <div class="spinner small"></div>
        <span>Переподключение...</span>
      </div>

      <div v-if="remotePeerName" class="peer-name">{{ remotePeerName }}</div>
    </div>

    <!-- Локальное видео (маленькое) -->
    <div class="video-wrapper local">
      <video
        ref="localVideoRef"
        autoplay
        playsinline
        muted
        :class="{ 'video-hidden': videoMuted || !hasVideo }"
      ></video>

      <!-- Плейсхолдер когда нет видео или камера выключена -->
      <div v-if="videoMuted || !hasVideo" class="video-placeholder-local">
        <div class="placeholder-icon">
          <i :class="videoMuted ? 'fas fa-video-slash' : 'fas fa-video'"></i>
        </div>
        <p v-if="!hasVideo">Нет камеры</p>
        <p v-else-if="videoMuted">Камера выключена</p>
      </div>

      <div v-if="localStream" class="peer-name">Вы ({{ displayName }})</div>
    </div>

    <!-- Скрытый аудио элемент для удаленного звука (Telegram WebView) -->
    <audio ref="remoteAudioRef" autoplay hidden></audio>
  </div>
</template>

<script setup lang="ts">
/**
 * Компонент контейнера видео
 * Отображает локальное и удаленное видео с плейсхолдерами
 */

import { ref, watch, onMounted, type Ref } from 'vue';

// ==================== PROPS ====================

interface Props {
  /** Локальный медиа поток */
  localStream: MediaStream | null;
  /** Удаленный медиа поток */
  remoteStream: MediaStream | null;
  /** Имя пользователя */
  displayName: string;
  /** Имя собеседника */
  remotePeerName: string;
  /** Флаг наличия видео */
  hasVideo: boolean;
  /** Флаг выключенного видео */
  videoMuted: boolean;
  /** Флаг переподключения */
  isReconnecting: boolean;
  /** Флаг выключенного видео у собеседника */
  remoteVideoMuted: boolean;
  /** Флаг выключенного аудио у собеседника */
  remoteAudioMuted: boolean;
}

const props = defineProps<Props>();

// ==================== EMITS ====================

const emit = defineEmits<{
  'toggle-controls': [];
  'mouse-move': [];
  'mouse-leave': [];
  'touch-start': [];
  'touch-end': [];
}>();

// ==================== REFS ====================

const localVideoRef: Ref<HTMLVideoElement | null> = ref(null);
const remoteVideoRef: Ref<HTMLVideoElement | null> = ref(null);
const remoteAudioRef: Ref<HTMLAudioElement | null> = ref(null);

// ==================== WATCHERS ====================

/** Следим за изменением локального потока */
watch(() => props.localStream, (stream) => {
  if (localVideoRef.value && stream) {
    localVideoRef.value.srcObject = stream;
  }
}, { immediate: true });

/** Следим за изменением удаленного потока */
watch(() => props.remoteStream, (stream) => {
  if (remoteVideoRef.value && stream) {
    remoteVideoRef.value.srcObject = stream;
  }
  // Также присваиваем удаленному аудио элементу (для Telegram WebView)
  if (remoteAudioRef.value && stream) {
    remoteAudioRef.value.srcObject = stream;
  }
}, { immediate: true });

// ==================== LIFECYCLE ====================

onMounted(() => {
  // Устанавливаем потоки при монтировании
  if (localVideoRef.value && props.localStream) {
    localVideoRef.value.srcObject = props.localStream;
  }
  if (remoteVideoRef.value && props.remoteStream) {
    remoteVideoRef.value.srcObject = props.remoteStream;
  }
  if (remoteAudioRef.value && props.remoteStream) {
    remoteAudioRef.value.srcObject = props.remoteStream;
  }
});
</script>

<style scoped>
.video-container {
  position: relative;
  width: 100%;
  height: 100%;
  /* Предотвращаем выделение текста при касании на мобильных устройствах */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

.video-wrapper {
  position: absolute;
  overflow: hidden;
  background: #1a1a1a;
  border-radius: 8px;
}

.video-wrapper.remote {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.video-wrapper.local {
  bottom: 80px;
  right: 20px;
  width: 240px;
  height: auto;
  border: 2px solid rgba(255, 255, 255, 0.2);
  z-index: 10;
}

video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.3s ease;
}

.video-wrapper.local video {
  transform: scaleX(-1);
  transform-origin: center;
}

video.video-hidden {
  opacity: 0;
  pointer-events: none;
}

.audio-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(20, 20, 20, 0.8));
}

.video-placeholder-local {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  background: linear-gradient(135deg, rgba(40, 40, 40, 0.95), rgba(60, 60, 60, 0.95));
  backdrop-filter: blur(10px);
  z-index: 1;
}

.video-placeholder-local p {
  margin: 0.5rem 0 0 0;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
}

.placeholder-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  margin-bottom: 0.5rem;
}

.placeholder-icon i {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.7);
}

.placeholder-icon-remote {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  margin-bottom: 1.5rem;
}

.placeholder-icon-remote i {
  font-size: 36px;
  color: rgba(255, 255, 255, 0.6);
}

.reconnecting-indicator {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background: rgba(251, 191, 36, 0.9);
  color: #000;
  font-weight: 600;
  border-radius: 8px;
  z-index: 10;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 1; }
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.spinner.small {
  width: 16px;
  height: 16px;
  border-width: 2px;
  margin-bottom: 0;
  margin-right: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.peer-name {
  position: absolute;
  bottom: 4px;
  left: 12px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .video-wrapper.local {
    width: 120px;
    height: 90px;
    bottom: 100px;
    right: 10px;
  }
}
</style>
